/**
 * Client analytics helper (#509).
 *
 * One thin, typed entry point for consent-gated client interaction events.
 * Server-side funnel milestones do NOT go through here - they are emitted
 * server-first into activity_logs (see backend funnel-emit + the signup-alert
 * edge function).
 *
 * Behaviour:
 *  1. No-op on the server (`typeof window === 'undefined'`).
 *  2. No-op unless analytics consent is granted AND GPC is not signalled -
 *     mirrors the gate in betterstack-consent-loader.tsx.
 *  3. Otherwise forwards independently to Better Stack, PostHog, and GA4.
 */

import { hasConsentFor, detectGpc } from './consent';
import { capturePostHogEvent } from './posthog';
import type { AnalyticsEventMap, AnalyticsEventName } from './analytics-events';
import type { PostHogEventMap, PostHogEventName } from './posthog-events';

type SharedAnalyticsEventName = Extract<AnalyticsEventName, PostHogEventName>;
type EnsureAnalyticsEventsMirroredInPostHog =
  AnalyticsEventName extends SharedAnalyticsEventName ? true : never;

// Compile-time guard: every typed client analytics event must also exist in PostHog.
const analyticsPostHogEventParity: EnsureAnalyticsEventsMirroredInPostHog = true;

/** True only when the user has allowed analytics and GPC is not set. */
export function analyticsAllowed(): boolean {
  if (typeof window === 'undefined') return false;
  return hasConsentFor('analytics') && !detectGpc();
}

/**
 * Emit a typed client analytics event. Silently no-ops on the server or when
 * consent is not granted; never throws.
 */
export function trackEvent<E extends AnalyticsEventName>(
  name: E,
  props: AnalyticsEventMap[E],
): void {
  void analyticsPostHogEventParity;
  if (!analyticsAllowed()) return;
  try {
    window.betterstack?.('track', name, props);
  } catch {
    // One telemetry vendor must never suppress another or break the UI.
  }
  try {
    capturePostHogEvent(
      name as SharedAnalyticsEventName,
      props as PostHogEventMap[SharedAnalyticsEventName],
    );
  } catch {
    // One telemetry vendor must never suppress another or break the UI.
  }
  try {
    window.gtag?.('event', name, props);
  } catch {
    // One telemetry vendor must never suppress another or break the UI.
  }
}
